import { useState, useEffect, useRef } from "react";
import { CheckCircle, XCircle, Search, RefreshCw, Trash2, Globe, AlertTriangle } from "lucide-react";
import * as api from "../lib/api";

// Official Google "G" logo SVG
const GoogleLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const PRIMARY = "#018D4C";

export default function GscSettingsCard({ onConnectionChange }) {
  const [propertyUrl, setPropertyUrl] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [info, setInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [refreshState, setRefreshState] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [showPropertyPicker, setShowPropertyPicker] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const pollRef = useRef(null);

  // `justConnected`: the viewer just came back from Google OAuth, so their own
  // property list may be shown even when a property is already saved.
  const reload = async ({ justConnected = false } = {}) => {
    setLoadingInfo(true);
    try {
      const s = await api.getGscSettings();
      setInfo(s);
      setLoadError(null);
      setPropertyUrl(s.property_url || "");
      setAutoRefresh(s.auto_refresh_enabled || false);
      onConnectionChange?.(s.verified && s.configured);
      // Otherwise only auto-load the list when no property is saved yet (first-time setup).
      // Avoids exposing the full personal Google property list to all org members.
      if (s.oauth_connected && (justConnected || !s.property_url)) {
        loadProperties();
        setShowPropertyPicker(true);
      }
    } catch (e) {
      console.error("[GSC] Load settings failed:", e);
      // Without this the card falls back to "Connect with Google" and hides a working connection.
      setLoadError(e.response?.data?.detail || e.message || "Request failed.");
    } finally {
      setLoadingInfo(false);
    }
  };

  const loadProperties = async () => {
    setPropertiesLoading(true);
    try {
      const { sites } = await api.getGscProperties();
      setProperties(sites || []);
    } catch (e) {
      setProperties([]);
    } finally {
      setPropertiesLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const justConnected = params.get("gsc_connected") === "1";
    reload({ justConnected });
    if (justConnected) {
      setSaveResult({ success: true, message: "Google account connected. Pick your property and click Verify & Save." });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("gsc_error")) {
      setSaveResult({ success: false, message: `OAuth error: ${params.get("gsc_error")}` });
      window.history.replaceState({}, "", window.location.pathname);
    }
    return () => pollRef.current && clearInterval(pollRef.current);
  }, []);

  const handleConnectGoogle = async () => {
    setOauthLoading(true);
    try {
      const { url } = await api.getGscOAuthUrl(window.location.href);
      window.location.href = url;
    } catch (e) {
      setSaveResult({ success: false, message: "Could not start OAuth flow. Check server configuration." });
      setOauthLoading(false);
    }
  };

  const handleSave = async e => {
    e.preventDefault();
    setSaving(true);
    setSaveResult(null);
    try {
      const res = await api.saveGscSettings({
        service_account_json: "",
        property_url: propertyUrl,
        auto_refresh_enabled: autoRefresh,
      });
      setSaveResult({ success: true, message: res.message || "GSC connected and verified.", warning: res.warning });
      await reload();
      if (res.refresh_started) {
        setRefreshing(true);
        setRefreshState({ status: "running", message: "Fetching last 28 days from Search Console…" });
        pollRefresh();
      }
    } catch (err) {
      setSaveResult({ success: false, message: err.response?.data?.detail || "Save failed." });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Disconnect Google Search Console and remove all stored search metrics?")) return;
    await api.deleteGscSettings();
    setPropertyUrl("");
    setAutoRefresh(false);
    setInfo(null);
    setSaveResult({ success: true, message: "GSC disconnected." });
  };

  const pollRefresh = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const st = await api.getGscRefreshStatus();
        setRefreshState(st);
        if (st.status !== "running") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setRefreshing(false);
          await reload();
        }
      } catch (e) { /* ignore */ }
    }, 1500);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshState({ status: "running", message: "Starting..." });
    try {
      await api.refreshGsc();
      pollRefresh();
    } catch (err) {
      setRefreshing(false);
      setRefreshState({ status: "failed", message: err.response?.data?.detail || "Refresh failed." });
    }
  };

  const isOAuth = info?.auth_method === "oauth";
  const isConnected = isOAuth && info?.oauth_connected;
  const isVerified = info?.verified && info?.configured;
  const lastRefresh = info?.last_refresh;

  return (
    <div data-testid="gsc-settings-card" className="bg-white rounded-xl border border-zinc-100 shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-zinc-100">
        <Search size={16} strokeWidth={2} className="text-zinc-900" />
        <div className="flex-1">
          <p className="text-sm font-bold text-zinc-900">
            Google Search Console
          </p>
          <p className="text-xs text-zinc-500">OAuth 2.0 — read-only search analytics</p>
        </div>
        {isVerified && (
          <span data-testid="gsc-status-pill" className="px-2 py-0.5 text-[10px] font-bold tracking-widest uppercase bg-green-100 text-green-700 rounded-full">
            CONNECTED
          </span>
        )}
        {isConnected && !isVerified && (
          <span data-testid="gsc-status-pill" className="px-2 py-0.5 text-[10px] font-bold tracking-widest uppercase bg-orange-100 text-orange-700 rounded-full">
            NEEDS PROPERTY
          </span>
        )}
      </div>

      <div className="p-5 space-y-4">
        {loadingInfo && !info && !loadError && (
          <p className="text-xs text-zinc-400">Loading…</p>
        )}

        {/* Load failure — don't mistake it for "not connected" */}
        {loadError && !info && (
          <div data-testid="gsc-load-error"
            className="flex items-start gap-2 px-4 py-3 text-sm border rounded-lg bg-red-50 border-red-200 text-red-600"
          >
            <XCircle size={16} strokeWidth={2} className="flex-shrink-0 mt-0.5" />
            <span className="flex-1">Couldn't load Search Console settings: {loadError}</span>
            <button type="button" onClick={() => reload()} disabled={loadingInfo}
              data-testid="gsc-load-retry"
              className="font-bold underline underline-offset-2 disabled:opacity-40"
            >
              {loadingInfo ? "Retrying…" : "Retry"}
            </button>
          </div>
        )}

        {/* Connected account info */}
        {isConnected && (
          <div className="bg-zinc-50 border border-zinc-100 rounded-lg px-4 py-3 text-xs space-y-1">
            <div className="flex items-center gap-1.5">
              <GoogleLogo size={14} />
              <span className="text-zinc-500">Signed in as</span>{" "}
              <code className="text-zinc-900">{info.service_account_email || "Google account"}</code>
            </div>
            {isVerified && (
              <div><span className="text-zinc-500">Property:</span> <code className="text-zinc-900">{info.property_url}</code></div>
            )}
            {lastRefresh?.refreshed_at && (
              <div className="pt-1">
                <span className="text-zinc-500">Last refresh:</span>{" "}
                <span className="text-green-700 font-bold">{new Date(lastRefresh.refreshed_at).toLocaleString()}</span>
                {" · "}
                <span className="text-zinc-900">{lastRefresh.pages_matched}/{lastRefresh.articles_total} articles matched</span>
              </div>
            )}
          </div>
        )}

        {/* Connect button (shown when no OAuth tokens yet) */}
        {!isConnected && !loadingInfo && !loadError && (
          <button
            type="button"
            onClick={handleConnectGoogle}
            disabled={oauthLoading}
            data-testid="gsc-oauth-connect-button"
            className="w-full flex items-center justify-center gap-2.5 px-4 py-3 text-sm font-bold bg-white border border-zinc-100 text-zinc-900 hover:bg-zinc-50 disabled:opacity-40 transition-colors rounded-lg"
          >
            <GoogleLogo size={18} />
            {oauthLoading ? "Redirecting to Google..." : "Connect with Google"}
          </button>
        )}

        {/* Property URL (shown once OAuth is connected) */}
        {isConnected && (
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-2">
                GSC Property URL <span className="text-red-500">*</span>
              </label>

              {/* Property picker — only shown when explicitly opened */}
              {!showPropertyPicker && propertyUrl && (
                <button
                  type="button"
                  onClick={async () => { setShowPropertyPicker(true); if (properties.length === 0) await loadProperties(); }}
                  className="mb-2 text-[10px] font-bold text-zinc-500 hover:text-zinc-900 underline underline-offset-2"
                  data-testid="gsc-change-property-button"
                >
                  Change property
                </button>
              )}
              {showPropertyPicker && propertiesLoading && (
                <div className="text-[11px] text-zinc-400 py-2">Loading your properties...</div>
              )}
              {showPropertyPicker && !propertiesLoading && properties.length > 0 && (
                <div className="mb-2 space-y-1" data-testid="gsc-property-list">
                  {properties.map(site => (
                    <button
                      key={site.siteUrl}
                      type="button"
                      onClick={() => { setPropertyUrl(site.siteUrl); setSaveResult(null); setShowPropertyPicker(false); }}
                      data-testid="gsc-property-option"
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs rounded-lg border transition-colors text-left ${
                        propertyUrl === site.siteUrl
                          ? "border-transparent text-white"
                          : "border-zinc-100 bg-white text-zinc-700 hover:bg-zinc-50"
                      }`}
                      style={propertyUrl === site.siteUrl ? { backgroundColor: PRIMARY, borderColor: PRIMARY } : {}}
                    >
                      <Globe size={12} strokeWidth={2} className="flex-shrink-0" />
                      <span className="font-mono truncate flex-1">{site.siteUrl}</span>
                      <span className={`text-[9px] font-bold tracking-widest uppercase flex-shrink-0 ${propertyUrl === site.siteUrl ? "text-white/70" : "text-zinc-400"}`}>
                        {site.permissionLevel?.replace("site", "") || ""}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Manual fallback input */}
              <input
                type="text"
                value={propertyUrl}
                onChange={e => { setPropertyUrl(e.target.value); setSaveResult(null); }}
                placeholder="https://yoursite.com/  or  sc-domain:yoursite.com"
                data-testid="gsc-property-url-input"
                className="w-full px-3 py-2.5 text-sm border border-zinc-100 bg-white focus:outline-none focus:ring-2 placeholder-zinc-400 font-mono rounded-lg"
                style={{ "--tw-ring-color": PRIMARY }}
              />
              {properties.length === 0 && !propertiesLoading && (
                <p className="text-[10px] text-zinc-400 mt-1">
                  Exact property URL from Search Console. Domain properties must use the <code>sc-domain:</code> prefix.
                </p>
              )}
            </div>

            {/* Auto-refresh toggle */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-zinc-900">Nightly Auto-Refresh</p>
                <p className="text-xs text-zinc-500 mt-0.5">Pull GSC data once a day</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={e => setAutoRefresh(e.target.checked)}
                  data-testid="gsc-auto-refresh-toggle"
                  className="sr-only peer"
                />
                <div className="w-10 h-5 bg-zinc-200 peer-checked:bg-zinc-900 transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:w-4 after:h-4 after:transition-all peer-checked:after:translate-x-5 rounded-full" />
              </label>
            </div>

            {/* Feedback */}
            {saveResult && (
              <div data-testid="gsc-save-result"
                className={`flex items-start gap-2 px-4 py-3 text-sm border rounded-lg ${saveResult.success ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-600"}`}
              >
                {saveResult.success ? <CheckCircle size={16} strokeWidth={2} className="flex-shrink-0 mt-0.5" /> : <XCircle size={16} strokeWidth={2} className="flex-shrink-0 mt-0.5" />}
                <span>{saveResult.message}</span>
              </div>
            )}
            {saveResult?.warning && (
              <div data-testid="gsc-duplicate-warning"
                className="flex items-start gap-2 px-4 py-3 text-sm border rounded-lg bg-amber-50 border-amber-200 text-amber-800"
              >
                <AlertTriangle size={16} strokeWidth={2} className="flex-shrink-0 mt-0.5" />
                <span>{saveResult.warning}</span>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <button type="submit" disabled={saving || !propertyUrl}
                data-testid="gsc-save-button"
                className="px-4 py-2.5 text-sm font-bold bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-40 transition-colors rounded-lg"
              >
                {saving ? "Verifying..." : "Verify & Save"}
              </button>
              {isVerified && (
                <button type="button" onClick={handleRefresh} disabled={refreshing}
                  data-testid="gsc-refresh-button"
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-40 transition-colors rounded-lg"
                  style={{ backgroundColor: PRIMARY }}
                >
                  <RefreshCw size={14} strokeWidth={2} className={refreshing ? "animate-spin" : ""} />
                  {refreshing ? "Refreshing..." : "Refresh GSC Data"}
                </button>
              )}
              <button type="button" onClick={handleConnectGoogle} disabled={oauthLoading}
                data-testid="gsc-reconnect-button"
                className="px-4 py-2.5 text-sm font-bold border border-zinc-100 text-zinc-500 hover:bg-zinc-50 disabled:opacity-40 transition-colors rounded-lg"
              >
                Reconnect
              </button>
              <button type="button" onClick={handleDelete}
                data-testid="gsc-delete-button"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-bold text-red-500 hover:bg-red-50 transition-colors ml-auto rounded-lg"
              >
                <Trash2 size={13} strokeWidth={2} /> Disconnect
              </button>
            </div>
          </form>
        )}

        {/* Save result when not connected (e.g. post-OAuth redirect message) */}
        {!isConnected && !loadError && saveResult && (
          <div data-testid="gsc-save-result"
            className={`flex items-start gap-2 px-4 py-3 text-sm border rounded-lg ${saveResult.success ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-600"}`}
          >
            {saveResult.success ? <CheckCircle size={16} strokeWidth={2} className="flex-shrink-0 mt-0.5" /> : <XCircle size={16} strokeWidth={2} className="flex-shrink-0 mt-0.5" />}
            <span>{saveResult.message}</span>
          </div>
        )}

        {/* Refresh status */}
        {refreshState && (
          <div data-testid="gsc-refresh-state"
            className={`px-4 py-3 text-xs border rounded-lg ${
              refreshState.status === "completed" ? "bg-green-50 border-green-200 text-green-700"
              : refreshState.status === "failed" ? "bg-red-50 border-red-200 text-red-600"
              : "bg-zinc-50 border-zinc-100 text-zinc-600"
            }`}
          >
            {refreshState.message}
            {refreshState.summary && (
              <div className="mt-1 font-bold">
                {refreshState.summary.pages_matched}/{refreshState.summary.articles_total} pages matched ·
                {" "}{refreshState.summary.site_total?.clicks ?? 0} clicks ·
                {" "}{refreshState.summary.site_total?.impressions ?? 0} impressions (28d)
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
